import React, { useCallback, useEffect, useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { ClipLoader } from 'react-spinners';

import { PasswordRequirements } from '../../../../components/PasswordRequirements';
import { StatusModal } from '../../../../components/StatusModal';
import { api } from '../../../../services/api';
import { passwordPolicyRegex } from '../../../../utils/PasswordPolicy';
import styles from './styles.module.scss';

interface IProps {
  onClose: () => void;
}

const PasswordForm = ({ onClose }: IProps) => {
  const [loading, setLoading] = useState(false);

  const [modal, setModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState<'success' | 'error'>('success');

  const [currentPass, setCurrentPass] = useState('');
  const [currentPassError, setCurrentPassError] = useState('');

  const [newPass, setNewPass] = useState('');
  const [newPassError, setNewPassError] = useState('');

  const [confirmNewPass, setConfirmNewPass] = useState('');
  const [confirmNewPassError, setConfirmNewPassError] = useState('');

  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmNewPass, setShowConfirmNewPass] = useState(false);

  const handleCurrentPass = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;

      if (value.length === 0) {
        setCurrentPassError('Preenchimento obrigatório');
      } else if (value.length < 8) {
        setCurrentPassError('A senha tem que ter no mínimo 8 caracteres');
      } else {
        setCurrentPassError('');
      }

      setCurrentPass(value);
    },
    []
  );

  const handleNewPassInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;

      if (value.length === 0) {
        setNewPassError('Preenchimento obrigatório');
      } else if (!value.match(passwordPolicyRegex)) {
        setNewPassError(
          'A senha deve ter 8+ caracteres, maiúscula, minúscula, número e símbolo'
        );
      } else {
        setNewPassError('');
      }

      setNewPass(value);
    },
    []
  );

  const handleConfirmPassInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;

      setConfirmNewPass(value);
    },
    []
  );

  // Compara Nova senha e Confirme senha
  useEffect(() => {
    if (confirmNewPass.length === 0) {
      setConfirmNewPassError('');
    } else if (newPass.localeCompare(confirmNewPass) !== 0) {
      setConfirmNewPassError('As senhas digitadas não coincidem');
    } else {
      setConfirmNewPassError('');
    }
  }, [newPass, confirmNewPass]);

  const formReady =
    currentPass.length >= 8 &&
    passwordPolicyRegex.test(newPass) &&
    newPass === confirmNewPass;

  const handleSubmitPass = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!formReady || loading) {
        return;
      }

      try {
        setLoading(true);

        const body = {
          password: currentPass,
          newPassword: newPass,
        };

        await api.post('/users/change-password', body);
        setModal(true);
        setModalType('success');
        setModalMessage('Senha alterada com sucesso!');
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ??
          'Erro ao realizar atualização da senha, tente novamente';

        setModal(true);
        setModalMessage(errorMessage);
        setModalType('error');
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [currentPass, newPass, formReady, loading]
  );

  return (
    <>
      {modal && (
        <StatusModal
          setModal={(value) => {
            setModal(value);
            if (modalType === 'success') {
              onClose();
            }
          }}
          text={modalMessage}
          type={modalType}
        />
      )}
      <form className={styles.form} onSubmit={handleSubmitPass}>
        <div className={styles.fields}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="currentPass">
              Senha atual
            </label>
            <div className={styles.input} data-error={!!currentPassError}>
              <input
                id="currentPass"
                type={showCurrentPass ? 'text' : 'password'}
                value={currentPass}
                onChange={handleCurrentPass}
              />
              <button
                type="button"
                className={styles.eyeButton}
                title={showCurrentPass ? 'Ocultar senha' : 'Mostrar senha'}
                aria-label={showCurrentPass ? 'Ocultar senha' : 'Mostrar senha'}
                onClick={() => setShowCurrentPass(!showCurrentPass)}
              >
                {showCurrentPass ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {currentPassError && (
              <p className={styles.error}>{currentPassError}</p>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="newPass">
              Nova senha
            </label>
            <div className={styles.input} data-error={!!newPassError}>
              <input
                id="newPass"
                type={showNewPass ? 'text' : 'password'}
                value={newPass}
                onChange={handleNewPassInput}
              />
              <button
                type="button"
                className={styles.eyeButton}
                title={showNewPass ? 'Ocultar senha' : 'Mostrar senha'}
                aria-label={showNewPass ? 'Ocultar senha' : 'Mostrar senha'}
                onClick={() => setShowNewPass(!showNewPass)}
              >
                {showNewPass ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {newPassError && <p className={styles.error}>{newPassError}</p>}
            <PasswordRequirements password={newPass} />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="confirmNewPass">
              Confirmar nova senha
            </label>
            <div className={styles.input} data-error={!!confirmNewPassError}>
              <input
                id="confirmNewPass"
                type={showConfirmNewPass ? 'text' : 'password'}
                value={confirmNewPass}
                disabled={!newPass}
                onChange={handleConfirmPassInput}
              />
              <button
                type="button"
                className={styles.eyeButton}
                title={showConfirmNewPass ? 'Ocultar senha' : 'Mostrar senha'}
                aria-label={
                  showConfirmNewPass ? 'Ocultar senha' : 'Mostrar senha'
                }
                onClick={() => setShowConfirmNewPass(!showConfirmNewPass)}
              >
                {showConfirmNewPass ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {confirmNewPassError && (
              <p className={styles.error}>{confirmNewPassError}</p>
            )}
          </div>
        </div>

        <div className={styles.buttons}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={!formReady || loading}
          >
            {loading ? <ClipLoader size={16} color="#fff" /> : 'Salvar'}
          </button>
        </div>
      </form>
    </>
  );
};

export { PasswordForm };
